/**
 * defectHelper.js
 * 
 * Central hub for defect form utilities - now organized into focused modules.
 * This file re-exports everything for backward compatibility.
 * 
 * Structure:
 * - helpers/validation.js: Validation schemas and format components
 * - helpers/calculations.js: Cost calculation functions
 * - helpers/dtos.js: Backend Data Transfer Objects
 * - helpers/database.js: Defect database search and apply functions
 * - This file: Display helpers, default values, and re-exports
 */

// ===== RE-EXPORTS FROM HELPER MODULES =====

// Validation (schemas + format components)
export { 
  validationSchema,
  NumericFormatCustom,
  NumberFormatForPercentage
} from './helpers/validation';

// Calculations (cost math)
export { 
  calulateTotal,
  convertToDecimal,
  calculateDistributedCosts
} from './helpers/calculations';

// DTOs (backend data transfer objects)
export { 
  createElementDto,
  createMeasureDto,
  createDefectDto
} from './helpers/dtos';

// Database (defect database functions)
export { 
  searchDefectsDatabase,
  getDefectSuggestionsByElement,
  applyDefectDatabaseToForm
} from './helpers/database';

// ===== DISPLAY HELPERS (Remain in this file) =====

export const getLanguageBasedOption = (option, langCode) => {
  if (option !== null && option !== "" && option.title !== undefined) {
    const tOption = option.title[langCode];
    if (tOption !== null) {
      return tOption;
    } else {
      return option.title["de"];
    }
  } else {
    return "";
  }
};

export const setDefaultValues = (defectType) =>{

  if(defectType ==="Lebenszyklus"){
    return {
      defectType : "Lebenszyklus",
      risk:"niedrig",
      priority:"langfristig",
      isRedFlag:false
    }
  } else if(defectType ==="Konformität"){
    return {
      defectType : "Konformität",
      risk:"hoch",
      priority:"kurzfristig",
      isRedFlag:true
    }
  } else if(defectType ==="Prävention"){
    return {
      defectType : "Prävention",
      risk:"niedrig",
      priority:"kurzfristig",
      isRedFlag:false
    }
  } else if(defectType ==="Hinweis"){
    return {
      defectType : "Hinweis",
      risk:"niedrig",
      priority:"kurzfristig",
      isRedFlag:false
    }
  }else{
    return {
      defectType : "Defekt",
      risk:"mittel",
      priority:"kurzfristig",
      isRedFlag:false
    }
  }

}
export const setDefaultRiskTypeValues = (riskType) =>{

  if(riskType ==="hoch"){
    return {
      isRedFlag:true
    }
  } else{
    return {
      isRedFlag:false
    }
  }
}
export const formData = {
  elementName: {
    title: "",
    language: "",
    value: "",
    parentIdent:"",
  },
  elementType: "", // Added for enhanced form compatibility
  isElementOther: false,
  customElement: {
    value: "",
    title: "", // Must be string, not object - matches old ElementSection.js
    parentIdent:"",
  },
  constructionYear: null,
  defectDescription: "",
  defectType: "Defekt",
  riskType: "mittel",
  priorityType: "kurzfristig", // Added for enhanced form compatibility
  inRedFlag: false,
  defectIdent: "",
  costDefect: true,
  dataRoomIndex: "",
  quelle: "",
  questionAndAnswer: "",
  qaField: "", // New Q&A field
  comments: "", // New comments field
  measureDescription: "",
  pricePerUnit: null,
  unit: "",
  quantity: 1,
  priority: "kurzfristig",
  isMultiple: false,
  multipleCost: {
    short: Number(30) / 100,
    medium: Number(40) / 100,
    long: Number(30) / 100,
  },
  percentageShortTermPriority: 30,
  percentageMidTermPriority: 40,
  percentageLongTermPriority: 30,
  projectFactor: "",
  elementAffected: "",
  probabilityOfOccurrence: "",
  extraCost:"",
  extraCostComment:"",
  costComment: "", // Added for enhanced form compatibility
  measureIdent: "",
  comments: "",
  totalCost: "",
  costSuggestions:[],
  costParameters: null,
  photos: [], // Added for enhanced form compatibility
};

// Factory function to create fresh form data (prevents reference sharing issues)
export const createFreshFormData = () => {
  return {
    elementName: {
      title: "",
      language: "",
      value: "",
      parentIdent: "",
    },
    elementType: "",
    isElementOther: false,
    customElement: {
      value: "",
      title: "", // Must be string, not object - matches old ElementSection.js
      parentIdent: "",
    },
    constructionYear: null,
    defectDescription: "",
    defectType: "Defekt",
    riskType: "mittel",
    priorityType: "kurzfristig",
    inRedFlag: false,
    defectIdent: "",
    costDefect: true,
    dataRoomIndex: "",
    quelle: "",
    questionAndAnswer: "",
    qaField: "",
    comments: "",
    measureDescription: "",
    pricePerUnit: null,
    unit: "",
    quantity: 1,
    priority: "kurzfristig",
    isMultiple: false,
    multipleCost: {
      short: Number(30) / 100,
      medium: Number(40) / 100,
      long: Number(30) / 100,
    },
    percentageShortTermPriority: 30,
    percentageMidTermPriority: 40,
    percentageLongTermPriority: 30,
    projectFactor: "",
    elementAffected: "",
    probabilityOfOccurrence: "",
    extraCost: "",
    extraCostComment: "",
    costComment: "",
    measureIdent: "",
    totalCost: "",
    costSuggestions: [],
    costParameters: null,
    photos: [],
  };
};

// ===== DISPLAY TEXT HELPERS (Remain in this file) =====
// Helper functions for display text in summary sections
export const getRiskTypeDisplayText = (value, riskTypeOptionsArray, projectLanguageCode) => {
  if (!value) return null; // Return null to let caller handle translation
  const risk = riskTypeOptionsArray.find(r => r.value === value);
  return risk ? getLanguageBasedOption(risk, projectLanguageCode) : value;
};

export const getDefectTypeDisplayText = (value, defectTypeOptions, projectLanguageCode) => {
  if (!value) return null; // Return null to let caller handle translation
  const type = defectTypeOptions.find(t => t.value === value);
  return type ? getLanguageBasedOption(type, projectLanguageCode) : value;
};

export const getPriorityTypeDisplayText = (value, priorityTypeOptions, projectLanguageCode) => {
  if (!value) return null; // Return null to let caller handle translation
  const priority = priorityTypeOptions.find(p => p.value === value);
  return priority ? getLanguageBasedOption(priority, projectLanguageCode) : value;
};
